/**
 * Frankfurter FX client + cache-key helpers — phase 3c of cost
 * tracking (ADR-0059). Free, no-key, ECB-backed daily rates;
 * coverage is ~30 fiat currencies. Long-tail currencies (UYU /
 * BTN / etc.) come back without a rate and surface as
 * "unconvertible" entries in the settlement view.
 *
 * Frankfurter publishes once daily after the ECB fixing. Past-
 * date rates are immutable, so the cache persists them forever
 * (see `fx-slice`). Future-dated bearers (trip planning) fall
 * back to today's rate via `effectiveFxDate` so the cache stays
 * small and the rate is at least directionally useful.
 */

const FRANKFURTER_BASE = 'https://api.frankfurter.app';

export function fxKey(date: string, from: string, to: string): string {
  return `${date}_${from.toUpperCase()}_${to.toUpperCase()}`;
}

/** Today in `YYYY-MM-DD`, UTC. */
export function fxToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Clamp a future date to today — Frankfurter doesn't quote
 *  future rates and the practical answer for a planned cost is
 *  "use today's rate as a directional estimate." */
export function effectiveFxDate(requestedDate: string): string {
  const today = fxToday();
  return requestedDate > today ? today : requestedDate;
}

/**
 * Fetch a rate for `(date, from, to)` from Frankfurter. Returns
 * `null` on:
 *  - network failure (offline / DNS / timeout)
 *  - non-2xx response (rate-limited, malformed)
 *  - currency outside Frankfurter's coverage (response has no
 *    `rates[to]` key)
 *
 * Trivially returns `1` when `from === to`. Today's date hits
 * `/latest`; past dates hit `/{date}`. Future dates should be
 * clamped via `effectiveFxDate` before calling.
 */
export async function fetchFxRate(
  date: string,
  from: string,
  to: string,
): Promise<number | null> {
  const FROM = from.trim().toUpperCase();
  const TO = to.trim().toUpperCase();
  if (!FROM || !TO) return null;
  if (FROM === TO) return 1;
  const today = fxToday();
  const endpoint = date >= today ? 'latest' : date;
  const url = `${FRANKFURTER_BASE}/${endpoint}?from=${FROM}&to=${TO}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as { rates?: Record<string, number> | undefined};
    const rate = data.rates?.[TO];
    return typeof rate === 'number' && Number.isFinite(rate) ? rate : null;
  } catch {
    return null;
  }
}
